/*
 * Discovery-Ansicht der Multi-Phase Discovery (concept C.10.a Phase C).
 * Plant-scoped: die Route liefert :id, die Session-Liste ist auf diese
 * Anlage gefiltert. Struktur ist gerätezentriert — die Anlage besteht
 * aus einigen Devices, jeder Regler bekommt seine eigene Discovery-
 * Karte mit Trigger-Knopf + Session-Historie. Kein "+ Neue Session"
 * mehr im Vordergrund (JSON-Paste ist Edge-Case).
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { StatusBadge } from '../components/ui/StatusBadge';
import { showToast } from '../components/Toaster';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import { showConfirm } from '../components/Confirmer';
import { ApplySchemaTemplateModal, type ApplyResult } from '../components/ApplySchemaTemplateModal';
import { isModbusFamily } from '../api/protocols';
import { isDeviceSchemaEmpty } from '../api/deviceSchema';
import type { PlantSchemaResponse, DeviceDto } from '../api/types';

interface Props { id?: string }

type DiscoveryStatusStr = 'Preview' | 'Committed' | 'Discarded';

interface DiscoverySessionSummary {
  id: string;
  plantId: string;
  deviceId: string | null;
  deviceIdentifier: string;
  status: DiscoveryStatusStr | number;
  createdAt: string;
  committedAt: string | null;
}

interface DiscoverySessionDetail {
  id: string;
  plantId: string;
  deviceId: string | null;
  deviceIdentifier: string;
  rawPayloadJson: string;
  previewJson: string;
  status: DiscoveryStatusStr | number;
  createdAt: string;
  updatedAt: string;
  committedAt: string | null;
  submittedBy: string | null;
  committedBy: string | null;
}

interface DiscoveryEnumMember {
  value: number;
  name: string;
  displayName: string | null;
}

interface DiscoveryLeafSuggestion {
  address: string;
  symbol: string;
  valueType: string;
  writable: boolean;
  unit: string | null;
  notes: string | null;
  enumMembers: DiscoveryEnumMember[] | null;
  // Parent hierarchy segments from the driver's walk (Climatix ViewNode
  // walker populates this; BACnet / Modbus leave it null). Drives the
  // tree render + subtree-prune in the review UI.
  path: string[] | null;
  minValue: number | null;
  maxValue: number | null;
}

interface DiscoveryPreview {
  suggestions: DiscoveryLeafSuggestion[];
  typicalMatches: { typicalId: string; name: string; version: number; confidence: number; reason: string }[];
  warnings: string[];
}

function statusLabel(status: DiscoveryStatusStr | number): DiscoveryStatusStr {
  if (typeof status === 'string') return status;
  return status === 1 ? 'Committed' : status === 2 ? 'Discarded' : 'Preview';
}
function statusTone(status: DiscoveryStatusStr | number): 'info' | 'ok' | 'neutral' {
  const s = statusLabel(status);
  return s === 'Preview' ? 'info' : s === 'Committed' ? 'ok' : 'neutral';
}

interface DeviceGroup {
  device: DeviceDto | null; // null = orphan (Session with no matched device)
  identifier: string;
  sessions: DiscoverySessionSummary[];
}

export function DiscoveryPage({ id: plantId }: Props) {
  const location = useLocation();
  // ?deviceId=… filter — set by OT-Devices-Table badge navigation. Filters
  // both the device-group list AND the raw sessions list, so the operator
  // sees only what they clicked on.
  const deviceIdFilter = (location.query as Record<string, string>).deviceId || null;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  // Watch state after a trigger to auto-highlight the new session when it lands.
  const [triggerWatch, setTriggerWatch] = useState<{ firedAt: string; deadlineMs: number } | null>(null);
  // J.17: Auf einem Modbus-Gerät ist „Register-Template wählen“ die Aktion,
  // die „Discovery starten“ hier ersetzt — Modbus hat auf Protokollebene
  // keine Register-Enumeration, ein Scan könnte nichts finden. Der State
  // hält das Device, für das der Template-Picker offen ist.
  const [applyTemplateFor, setApplyTemplateFor] = useState<DeviceDto | null>(null);

  const plantState = useAsync(
    (signal) => plantId
      ? api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, signal)
      : Promise.resolve<PlantSchemaResponse | null>(null),
    [plantId],
  );
  const sessionsState = useAsync(
    (signal) => plantId
      ? api.get<DiscoverySessionSummary[]>(`/api/discovery/sessions?plantId=${plantId}`, signal)
      : Promise.resolve<DiscoverySessionSummary[]>([]),
    [plantId, tick],
  );

  // Watch-loop: after "Discovery starten", poll for new sessions and pop the
  // fresh one open so the operator sees the preview without hunting.
  useEffect(() => {
    if (!triggerWatch) return undefined;
    const timer = window.setInterval(() => {
      if (Date.now() > triggerWatch.deadlineMs) {
        setTriggerWatch(null);
        showToast({
          tone: 'warn',
          title: 'Kein Client-Push binnen 90 s',
          message: 'Trigger gesendet, aber kein Field-Client hat gemeldet. Prüfe Systemd-Log am Client oder ob der Discovery-Trigger-Subscriber verbunden ist.',
          autoHideAfterMs: 8000,
        });
        return;
      }
      setTick((n) => n + 1);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [triggerWatch]);
  useEffect(() => {
    if (!triggerWatch) return;
    if (sessionsState.status !== 'ok') return;
    const arrived = sessionsState.data.find((s) => s.createdAt > triggerWatch.firedAt);
    if (!arrived) return;
    setTriggerWatch(null);
    setSelectedId(arrived.id);
    showToast({
      tone: 'success',
      title: 'Discovery-Session eingetroffen',
      message: `${arrived.deviceIdentifier} — Preview prüfen und ggf. Commit klicken.`,
      autoHideAfterMs: 6000,
    });
  }, [sessionsState, triggerWatch]);

  const devices = plantState.status === 'ok' && plantState.data ? plantState.data.devices : [];
  const allSessions = sessionsState.status === 'ok' ? sessionsState.data : [];
  const sessions = deviceIdFilter
    ? allSessions.filter((s) => s.deviceId === deviceIdFilter)
    : allSessions;
  const plantName = plantState.status === 'ok' && plantState.data ? plantState.data.name : '';

  const groups: DeviceGroup[] = useMemo(() => {
    const byDeviceId = new Map<string, DiscoverySessionSummary[]>();
    const orphans = new Map<string, DiscoverySessionSummary[]>();
    for (const s of sessions) {
      if (s.deviceId) {
        const list = byDeviceId.get(s.deviceId) ?? [];
        list.push(s);
        byDeviceId.set(s.deviceId, list);
      } else {
        const list = orphans.get(s.deviceIdentifier) ?? [];
        list.push(s);
        orphans.set(s.deviceIdentifier, list);
      }
    }
    const sortByDateDesc = (a: DiscoverySessionSummary, b: DiscoverySessionSummary) =>
      a.createdAt < b.createdAt ? 1 : -1;
    const result: DeviceGroup[] = [];
    for (const d of devices) {
      const s = (byDeviceId.get(d.id) ?? []).sort(sortByDateDesc);
      result.push({ device: d, identifier: d.name, sessions: s });
    }
    for (const [ident, s] of orphans) {
      result.push({ device: null, identifier: ident, sessions: s.sort(sortByDateDesc) });
    }
    return result;
  }, [devices, sessions]);

  const triggerFor = async (deviceId: string | null, deviceLabel: string) => {
    if (!plantId) return;
    try {
      const qs = deviceId ? `?deviceId=${deviceId}` : '';
      await api.post<{ correlationId: string }>(
        `/api/plants/${plantId}/discovery/trigger${qs}`, {});
      const firedAt = new Date().toISOString();
      showToast({
        tone: 'info',
        title: 'Discovery gestartet',
        message: `${deviceLabel} — Field-Client meldet die neue Session automatisch.`,
        autoHideAfterMs: 6000,
      });
      setTriggerWatch({ firedAt, deadlineMs: Date.now() + 90_000 });
    } catch (e) {
      const msg = e instanceof ApiError ? `Trigger fehlgeschlagen (${e.status}).` : 'Unbekannter Fehler.';
      showToast({ tone: 'error', title: 'Discovery-Trigger fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
    }
  };

  const onTemplateApplied = (result: ApplyResult) => {
    setApplyTemplateFor(null);
    showToast({
      tone: 'success',
      title: 'Register-Template angewendet',
      message: `${result.nodesCreated} Gruppe(n), ${result.leavesCreated} Register angelegt`
        + (result.leavesDeleted > 0 ? ` — ${result.leavesDeleted} ersetzt.` : '.'),
      autoHideAfterMs: 6000,
    });
    setTick((n) => n + 1);
  };

  // Der Template-Picker wird an beiden Ansichten dieser Seite gebraucht
  // (Fokus-Ansicht und Übersichtskarten), deshalb steht er einmal hier.
  const templateModal = applyTemplateFor !== null && plantId
    ? (
      <ApplySchemaTemplateModal
        plantId={plantId}
        deviceId={applyTemplateFor.id}
        deviceProtocol={applyTemplateFor.protocol}
        isTargetEmpty={isDeviceSchemaEmpty(applyTemplateFor)}
        onClose={() => setApplyTemplateFor(null)}
        onApplied={onTemplateApplied}
      />
    )
    : null;

  if (!plantId) {
    return <AppShell title="Discovery"><p class="muted">Anlage aus der Route fehlt.</p></AppShell>;
  }

  // Focused single-device mode — activated when the OT-Devices row (or any
  // other deep link) passes ?deviceId=…. Ditches the two-panel master/
  // detail layout in favour of a full-width tree. Everything else on this
  // page is fallback for the overview case.
  if (deviceIdFilter) {
    const device = devices.find((d) => d.id === deviceIdFilter) ?? null;
    return (
      <>
        <FocusedDeviceDiscovery
          plantId={plantId}
          plantName={plantName}
          device={device}
          sessions={sessions}
          selectedId={selectedId}
          onSelectSession={setSelectedId}
          onTrigger={() => triggerFor(deviceIdFilter, device?.name ?? deviceIdFilter)}
          onApplyTemplate={() => device && setApplyTemplateFor(device)}
          onChanged={() => setTick((n) => n + 1)}
          onDeleted={() => { setSelectedId(null); setTick((n) => n + 1); }}
          triggerWatchActive={triggerWatch !== null}
          tick={tick}
        />
        {templateModal}
      </>
    );
  }

  return (
    <AppShell
      title="Discovery"
      actions={
        <label class="row-2" style={{ fontSize: 'var(--fs-sm)' }}>
          <input type="checkbox" checked={showHistory}
                 onChange={(e) => setShowHistory((e.target as HTMLInputElement).checked)} />
          <span class="muted">Committed/Verworfen mitzeigen</span>
        </label>
      }
    >
      <PlantSubNav plantId={plantId} plantName={plantName} />
      <div class="hv-plant-discovery">
        <div class="hv-plant-discovery__devices">
          {plantState.status === 'loading' && <p class="muted">Lade Anlagen-Struktur…</p>}
          {plantState.status === 'ok' && devices.length === 0 && (
            <div class="muted" style={{ padding: 'var(--space-4)' }}>
              <p>
                <strong>Diese Anlage hat noch keine Devices.</strong>
              </p>
              <p>
                Erst über <em>Netzwerk-Scan</em> das lokale Segment abklopfen und
                die gefundenen Kandidaten klassifizieren — dann erscheinen die
                Regler hier und kannst du Discovery pro Regler auslösen.
              </p>
            </div>
          )}
          {plantState.status === 'ok' && groups.map((g) => (
            <DeviceCard key={g.device?.id ?? g.identifier}
                        group={g}
                        showHistory={showHistory}
                        selectedSessionId={selectedId}
                        onTrigger={() => triggerFor(g.device?.id ?? null, g.identifier)}
                        onApplyTemplate={() => g.device && setApplyTemplateFor(g.device)}
                        onSelectSession={(id) => setSelectedId(id)} />
          ))}
        </div>
        <div class="hv-plant-discovery__detail">
          {selectedId ? (
            <DiscoveryDetail
              key={`${selectedId}:${tick}`}
              sessionId={selectedId}
              onChanged={() => setTick((n) => n + 1)}
              onDeleted={() => { setSelectedId(null); setTick((n) => n + 1); }}
            />
          ) : (
            <p class="muted" style={{ padding: 'var(--space-4)' }}>Session wählen um Preview anzuzeigen.</p>
          )}
        </div>
      </div>
      {templateModal}
    </AppShell>
  );
}

// -------- Focused single-device Discovery view -----------------------------
//
// When the operator clicks "Discovery" on an OT-Devices row we land here with
// ?deviceId=… in the URL. Layout is a full-width header (device + trigger +
// session picker) over a full-width tree — no left/right split, no
// multi-device cards. That's the layout every "give me the DPs of this
// controller" flow actually wants.

interface FocusedProps {
  plantId: string;
  plantName: string;
  device: DeviceDto | null;
  sessions: DiscoverySessionSummary[];
  selectedId: string | null;
  onSelectSession(id: string | null): void;
  onTrigger(): void;
  /** J.17: Ersatz für onTrigger auf Geräten ohne explorative Discovery. */
  onApplyTemplate(): void;
  onChanged(): void;
  onDeleted(): void;
  triggerWatchActive: boolean;
  tick: number;
}

function FocusedDeviceDiscovery({
  plantId, plantName, device, sessions, selectedId,
  onSelectSession, onTrigger, onApplyTemplate, onChanged, onDeleted, triggerWatchActive, tick,
}: FocusedProps) {
  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
    [sessions],
  );

  // Auto-select the freshest preview when the operator lands here without a
  // pre-selected session. Preview > Committed > Discarded so the operator
  // sees the pending review first.
  useEffect(() => {
    if (selectedId) return;
    if (sortedSessions.length === 0) return;
    const preview = sortedSessions.find((s) => statusLabel(s.status) === 'Preview');
    onSelectSession((preview ?? sortedSessions[0]).id);
  }, [selectedId, sortedSessions, onSelectSession]);

  const deviceLabel = device?.name ?? 'Unbekanntes Device';
  const protocol = device?.protocol ?? null;
  // Modbus kennt keine Register-Enumeration — hier stand bis jetzt ein
  // „Discovery starten“, das auf diesem Gerät nie etwas liefern konnte.
  const isModbus = isModbusFamily(protocol);

  return (
    <AppShell title="Discovery">
      <PlantSubNav plantId={plantId} plantName={plantName} />
      <div class="hv-focused-discovery">
        <header class="hv-focused-discovery__head">
          <a href={`/plants/${plantId}/ot/devices`} class="hv-focused-discovery__back" title="Zurück zur Device-Liste">
            ← OT-Devices
          </a>
          <div class="hv-focused-discovery__title">
            <h2>{deviceLabel}</h2>
            {device?.symbol && <span class="mono muted">{device.symbol}</span>}
            {protocol && <StatusBadge tone="neutral">{protocol}</StatusBadge>}
          </div>
          {isModbus ? (
            <button type="button"
                    class="hv-btn hv-btn--primary"
                    title={'Modbus hat keine Discovery — es gibt kein Protokoll, das die Register '
                      + 'aufzählt. Ein Register-Template trägt die Register-Karte der Geräteklasse '
                      + 'und wird ohne Netzabfrage in den Baum materialisiert.'}
                    onClick={onApplyTemplate}>
              Register-Template wählen…
            </button>
          ) : (
            <button type="button"
                    class="hv-btn hv-btn--primary"
                    disabled={triggerWatchActive}
                    onClick={onTrigger}>
              {triggerWatchActive ? 'Warte auf Client-Push…' : 'Discovery starten'}
            </button>
          )}
        </header>
        {isModbus && (
          <p class="muted">
            <strong>{protocol}</strong> hat auf Protokollebene keine Register-Enumeration —
            es gibt keine Frage „welche Register hast du". Ein Scan könnte hier nichts
            finden; der Weg zu einem Baum ist ein <strong>Register-Template</strong>
            {' '}(Adressen, Datentypen, Skalierung, Einheiten, Gruppierung), das ohne
            Netzabfrage angewendet wird. Templates für <code>modbus-tcp</code> und
            {' '}<code>modbus-rtu</code> gelten als eine Familie.
          </p>
        )}
        <div class="hv-focused-discovery__sessions">
          {sortedSessions.length === 0 && !isModbus && (
            <p class="muted">Noch keine Sessions. Klick <em>Discovery starten</em>, damit der Field-Client die DPs meldet.</p>
          )}
          {sortedSessions.map((s) => (
            <button key={s.id} type="button"
                    class={`hv-focused-discovery__tab${s.id === selectedId ? ' hv-focused-discovery__tab--active' : ''}`}
                    onClick={() => onSelectSession(s.id)}>
              <StatusBadge tone={statusTone(s.status)}>{statusLabel(s.status)}</StatusBadge>
              <span class="muted">{formatTimestamp(s.createdAt)}</span>
            </button>
          ))}
        </div>
        <div class="hv-focused-discovery__body">
          {selectedId ? (
            <DiscoveryDetail
              key={`${selectedId}:${tick}`}
              sessionId={selectedId}
              onChanged={onChanged}
              onDeleted={onDeleted}
            />
          ) : sortedSessions.length > 0 ? (
            <p class="muted">Session oben wählen um Preview anzuzeigen.</p>
          ) : null}
        </div>
      </div>
    </AppShell>
  );
}

interface DeviceCardProps {
  group: DeviceGroup;
  showHistory: boolean;
  selectedSessionId: string | null;
  onTrigger(): void;
  /** J.17: Ersatz für onTrigger auf Geräten ohne explorative Discovery. */
  onApplyTemplate(): void;
  onSelectSession(id: string): void;
}
function DeviceCard({
  group, showHistory, selectedSessionId, onTrigger, onApplyTemplate, onSelectSession,
}: DeviceCardProps) {
  const preview = group.sessions.filter((s) => statusLabel(s.status) === 'Preview');
  const rest = group.sessions.filter((s) => statusLabel(s.status) !== 'Preview');
  const label = group.device ? group.device.name : `${group.identifier} (kein Device)`;
  const protocol = group.device?.protocol ?? null;
  const isModbus = isModbusFamily(protocol);
  return (
    <div class="hv-plant-discovery__device">
      <div class="hv-plant-discovery__device-head">
        <div class="hv-plant-discovery__device-title">
          <strong>{label}</strong>
          {group.device && (
            <span class="mono muted" style={{ marginLeft: '0.5em' }}>{group.device.symbol}</span>
          )}
          {protocol && <StatusBadge tone="neutral">{protocol}</StatusBadge>}
          {preview.length > 0 && <StatusBadge tone="info">{preview.length} offen</StatusBadge>}
        </div>
        {group.device && (isModbus ? (
          <button type="button" class="hv-btn hv-btn--primary"
                  title={'Modbus hat keine Discovery — es gibt kein Protokoll, das die Register '
                    + 'aufzählt. Stattdessen die Register-Karte der Geräteklasse anwenden.'}
                  onClick={onApplyTemplate}>
            Register-Template wählen…
          </button>
        ) : (
          <button type="button" class="hv-btn hv-btn--primary" onClick={onTrigger}>
            Discovery starten
          </button>
        ))}
      </div>
      {preview.length === 0 && rest.length === 0 && (
        <p class="muted" style={{ margin: '4px 0 0' }}>
          {isModbus
            ? 'Modbus hat keine Discovery — hier entsteht der Baum aus einem Register-Template, nicht aus einem Scan.'
            : 'Noch keine Sessions. Klick „Discovery starten“, damit der Field-Client die Datenpunkte meldet.'}
        </p>
      )}
      {preview.length > 0 && (
        <ul class="hv-plant-discovery__sessions">
          {preview.map((s) => (
            <SessionRow key={s.id} session={s}
                        selected={s.id === selectedSessionId}
                        onSelect={() => onSelectSession(s.id)} />
          ))}
        </ul>
      )}
      {showHistory && rest.length > 0 && (
        <details class="hv-plant-discovery__history">
          <summary class="muted">Historie ({rest.length})</summary>
          <ul class="hv-plant-discovery__sessions">
            {rest.map((s) => (
              <SessionRow key={s.id} session={s}
                          selected={s.id === selectedSessionId}
                          onSelect={() => onSelectSession(s.id)} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

interface SessionRowProps {
  session: DiscoverySessionSummary;
  selected: boolean;
  onSelect(): void;
}
function SessionRow({ session, selected, onSelect }: SessionRowProps) {
  const s = statusLabel(session.status);
  return (
    <li>
      <button type="button" class={`hv-plant-discovery__session${selected ? ' hv-plant-discovery__session--active' : ''}`}
              onClick={onSelect}>
        <StatusBadge tone={statusTone(session.status)}>{s}</StatusBadge>
        <span class="mono muted">{formatTimestamp(session.createdAt)}</span>
        {s === 'Committed' && session.committedAt && (
          <span class="muted">→ commit {formatTimestamp(session.committedAt)}</span>
        )}
      </button>
    </li>
  );
}

// ---- Detail panel (unverändert übernommen aus altem Master-Detail-Slice) -----
interface DetailProps {
  sessionId: string;
  onChanged(): void;
  onDeleted(): void;
}
function DiscoveryDetail({ sessionId, onChanged, onDeleted }: DetailProps) {
  const [busy, setBusy] = useState<false | 'commit' | 'discard' | 'delete'>(false);
  const [error, setError] = useState<string | null>(null);
  // Addresses the operator has toggled off in the review tree. Commit sends
  // this list so the server drops those suggestions before materialisation.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const detailState = useAsync(
    (signal) => api.get<DiscoverySessionDetail>(`/api/discovery/sessions/${sessionId}`, signal),
    [sessionId],
  );
  const preview = useMemo(() => {
    if (detailState.status !== 'ok') return null;
    try { return JSON.parse(detailState.data.previewJson) as DiscoveryPreview; }
    catch { return null; }
  }, [detailState]);
  // Session switch → clear the excluded set (would otherwise carry a
  // pruning intent from the previous session into a totally different tree).
  useEffect(() => { setExcluded(new Set()); }, [sessionId]);

  if (detailState.status === 'loading') return <p class="muted">Lade Session…</p>;
  if (detailState.status === 'error') return <p class="muted">Session konnte nicht geladen werden.</p>;

  const detail = detailState.data;
  const status = statusLabel(detail.status);
  const isPreview = status === 'Preview';

  async function commit() {
    setBusy('commit'); setError(null);
    try {
      const body = excluded.size > 0
        ? { excludedAddresses: Array.from(excluded) }
        : {};
      const res = await api.post<{ leavesCreated: number; deviceCreated: boolean }>(
        `/api/discovery/sessions/${sessionId}/commit`, body);
      const parts: string[] = [];
      if (res.deviceCreated) parts.push('Device neu angelegt');
      if (res.leavesCreated > 0) parts.push(`${res.leavesCreated} neue Leaf${res.leavesCreated === 1 ? '' : 's'}`);
      if (excluded.size > 0) parts.push(`${excluded.size} Vorschlag/Vorschläge ausgeschlossen`);
      showToast({ tone: 'success', title: 'Discovery committed',
        message: parts.length > 0 ? parts.join(' · ') : 'Keine neuen Rows — Session war schon materialisiert.',
        autoHideAfterMs: 6000 });
      onChanged();
    } catch (err) {
      // Structured server errors carry {error, details} in the JSON body;
      // fall back to err.message otherwise. Way more useful than the raw
      // 'POST /api/... -> 500' text that used to leak into the UI.
      if (err instanceof ApiError && err.body && typeof err.body === 'object') {
        const bodyObj = err.body as { error?: string; details?: string };
        const parts: string[] = [];
        if (bodyObj.error) parts.push(bodyObj.error);
        if (bodyObj.details) parts.push(bodyObj.details);
        parts.push(`HTTP ${err.status}`);
        setError(parts.join('\n'));
      } else {
        setError(err instanceof Error ? err.message : 'Commit fehlgeschlagen.');
      }
    }
    finally { setBusy(false); }
  }
  async function discard() {
    const ok = await showConfirm({
      title: 'Session verwerfen?',
      message: 'Wird als "Discarded" markiert, die Row bleibt als Audit-Eintrag erhalten.',
      confirmLabel: 'Verwerfen',
      danger: true,
    });
    if (!ok) return;
    setBusy('discard'); setError(null);
    try { await api.post(`/api/discovery/sessions/${sessionId}/discard`, {}); onChanged(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Verwerfen fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  async function del() {
    const ok = await showConfirm({
      title: 'Session löschen?',
      message: 'Unwiderruflich. Bei Committed-Sessions bleibt die Materialisierung im Baum bestehen.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    setBusy('delete'); setError(null);
    try { await api.delete(`/api/discovery/sessions/${sessionId}`); onDeleted(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.'); setBusy(false); }
  }

  return (
    <div class="hv-discovery__panel">
      <header class="hv-discovery__panel-head">
        <div>
          <h2 class="hv-discovery__panel-title">{detail.deviceIdentifier}</h2>
          <div class="muted">
            Plant <code class="mono">{shortGuid(detail.plantId)}</code>
            {detail.deviceId ? <> · Device <code class="mono">{shortGuid(detail.deviceId)}</code></>
                             : <> · Gerät noch nicht angelegt</>}
          </div>
        </div>
        <div class="hv-discovery__panel-actions">
          <StatusBadge tone={statusTone(detail.status)}>{status}</StatusBadge>
          <button type="button" class="hv-btn" disabled={!isPreview || busy !== false} onClick={discard}>
            {busy === 'discard' ? 'Verwerfe…' : 'Verwerfen'}
          </button>
          <button type="button" class="hv-btn hv-btn--primary" disabled={!isPreview || busy !== false} onClick={commit}>
            {busy === 'commit' ? 'Committe…' : 'Commit'}
          </button>
          <button type="button" class="hv-btn hv-btn--danger" disabled={busy !== false} onClick={del}
                  title="Session unwiderruflich löschen">
            {busy === 'delete' ? 'Lösche…' : 'Löschen'}
          </button>
        </div>
      </header>
      {error && (
        <div class="hv-discovery__errorbox" role="alert">
          <div class="hv-discovery__errorbox-title">Commit fehlgeschlagen</div>
          <pre class="hv-discovery__errorbox-body">{error}</pre>
        </div>
      )}
      {preview ? (
        <>
          <section class="hv-discovery__card">
            <header class="hv-discovery__card-head">
              <h3 class="hv-discovery__card-title">
                Vorschau
                <span class="hv-discovery__card-title-badge">
                  {preview.suggestions.length - excluded.size} von {preview.suggestions.length}
                </span>
                {excluded.size > 0 && (
                  <span class="hv-discovery__card-title-note">
                    · {excluded.size} ausgeschlossen
                  </span>
                )}
              </h3>
              {isPreview && excluded.size > 0 && (
                <button type="button" class="hv-btn hv-btn--tight"
                        onClick={() => setExcluded(new Set())}>
                  Alle wieder einbeziehen
                </button>
              )}
            </header>
            <div class="hv-discovery__card-body">
              <PreviewTree suggestions={preview.suggestions}
                           excluded={excluded}
                           readOnly={!isPreview}
                           onToggle={(addresses, include) => {
                             const next = new Set(excluded);
                             for (const a of addresses) {
                               if (include) next.delete(a);
                               else next.add(a);
                             }
                             setExcluded(next);
                           }} />
            </div>
          </section>
          {preview.warnings.length > 0 && (
            <section class="hv-discovery__card hv-discovery__card--warnings">
              <header class="hv-discovery__card-head">
                <h3 class="hv-discovery__card-title">
                  Hinweise vom Client
                  <span class="hv-discovery__card-title-badge">{preview.warnings.length}</span>
                </h3>
              </header>
              <div class="hv-discovery__card-body">
                <ul class="hv-discovery__warnings">
                  {preview.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              </div>
            </section>
          )}
        </>
      ) : (
        <section class="hv-discovery__card">
          <div class="hv-discovery__card-body">
            <p class="muted">Vorschau konnte nicht gelesen werden (kein valides JSON).</p>
          </div>
        </section>
      )}
      <details class="hv-discovery__raw">
        <summary>Roh-Payload anzeigen</summary>
        <pre class="mono">{formatJson(detail.rawPayloadJson)}</pre>
      </details>
    </div>
  );
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => n.toString().padStart(2, '0');
  const yy = pad(d.getFullYear() % 100);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${yy} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function shortGuid(g: string): string { return g.length > 8 ? g.slice(0, 8) + '…' : g; }
function formatJson(source: string): string {
  try { return JSON.stringify(JSON.parse(source), null, 2); } catch { return source; }
}

// ---- Preview Tree (grouped by Path) ---------------------------------------
//
// A discovery-run against a Climatix C600 easily produces 5000+ suggestions;
// a flat table is unusable at that size. The driver populates
// `RawAddress.Path` with the walked ViewNode hierarchy (root-first,
// leaf-excluded), we group by prefix here and render a collapsible tree
// with subtree-checkboxes so the operator can prune whole branches before
// commit. Excluded addresses travel to the commit endpoint verbatim.

interface TreeNode {
  label: string;
  fullPath: readonly string[];   // root → this node, empty for the virtual root
  children: Map<string, TreeNode>;
  leaves: DiscoveryLeafSuggestion[];
  // Cached union of every leaf address anywhere in this subtree — computed
  // once during build so tri-state selection stays O(1) per node instead of
  // walking the tree on every checkbox click.
  addresses: string[];
}

/**
 * Fallback path derivation for sessions collected by a driver build that
 * predates the RawAddress.Path field (server-side field is null there). The
 * Climatix driver already put the canonical hierarchy into `notes` in the
 * shape "Automation stat.\\System\\System clock\\Hour" — split on backslash
 * and drop the last segment (that's the leaf's own display name) so the
 * remainder is the parents. Backslash is the wire separator used by
 * Climatix' HierarchyName. Once every field-client has been redeployed with
 * driver-populated Path this branch becomes dead code and can go.
 */
function derivePath(s: DiscoveryLeafSuggestion): readonly string[] {
  if (s.path && s.path.length > 0) return s.path;
  const notes = s.notes;
  if (!notes) return [];
  if (!notes.includes('\\')) return [];
  const segments = notes.split(/\\+/).map((p) => p.trim()).filter((p) => p.length > 0);
  if (segments.length <= 1) return [];
  return segments.slice(0, -1);
}

function buildTree(suggestions: readonly DiscoveryLeafSuggestion[]): TreeNode {
  const root: TreeNode = {
    label: '',
    fullPath: [],
    children: new Map(),
    leaves: [],
    addresses: [],
  };
  for (const s of suggestions) {
    let node = root;
    const path = derivePath(s);
    for (const raw of path) {
      const seg = raw.trim();
      if (!seg) continue;
      let child = node.children.get(seg);
      if (!child) {
        child = {
          label: seg,
          fullPath: [...node.fullPath, seg],
          children: new Map(),
          leaves: [],
          addresses: [],
        };
        node.children.set(seg, child);
      }
      node = child;
    }
    node.leaves.push(s);
  }
  // Post-order fill the `addresses` cache.
  const fill = (n: TreeNode): void => {
    const acc: string[] = [];
    for (const child of n.children.values()) {
      fill(child);
      acc.push(...child.addresses);
    }
    for (const l of n.leaves) acc.push(l.address);
    n.addresses = acc;
  };
  fill(root);
  return root;
}

interface PreviewTreeProps {
  suggestions: readonly DiscoveryLeafSuggestion[];
  excluded: ReadonlySet<string>;
  readOnly: boolean;
  onToggle(addresses: readonly string[], include: boolean): void;
}
function PreviewTree({ suggestions, excluded, readOnly, onToggle }: PreviewTreeProps) {
  const tree = useMemo(() => buildTree(suggestions), [suggestions]);
  return (
    <div class="hv-discovery__tree">
      <TreeChildren node={tree} depth={0}
                    excluded={excluded}
                    readOnly={readOnly}
                    onToggle={onToggle} />
    </div>
  );
}

interface TreeChildrenProps {
  node: TreeNode;
  depth: number;
  excluded: ReadonlySet<string>;
  readOnly: boolean;
  onToggle(addresses: readonly string[], include: boolean): void;
}
function TreeChildren({ node, depth, excluded, readOnly, onToggle }: TreeChildrenProps) {
  const childrenSorted = useMemo(
    () => Array.from(node.children.values()).sort((a, b) => a.label.localeCompare(b.label)),
    [node.children],
  );
  const leavesSorted = useMemo(
    () => [...node.leaves].sort((a, b) => a.symbol.localeCompare(b.symbol)),
    [node.leaves],
  );
  return (
    <ul class="hv-discovery__tree-list" style={{ paddingLeft: depth === 0 ? '0' : '1.2em' }}>
      {childrenSorted.map((child) => (
        <TreeFolder key={child.label} node={child} depth={depth + 1}
                    excluded={excluded}
                    readOnly={readOnly}
                    onToggle={onToggle} />
      ))}
      {leavesSorted.map((leaf) => (
        <TreeLeaf key={leaf.address} leaf={leaf}
                  excluded={excluded.has(leaf.address)}
                  readOnly={readOnly}
                  onToggle={(include) => onToggle([leaf.address], include)} />
      ))}
    </ul>
  );
}

interface TreeFolderProps {
  node: TreeNode;
  depth: number;
  excluded: ReadonlySet<string>;
  readOnly: boolean;
  onToggle(addresses: readonly string[], include: boolean): void;
}
function TreeFolder({ node, depth, excluded, readOnly, onToggle }: TreeFolderProps) {
  // Auto-collapse deep subtrees so a 5000-DP tree opens compact — the
  // operator drills in only where needed. Depth 1 = top folders stay open.
  const [open, setOpen] = useState(depth <= 1);
  const total = node.addresses.length;
  const excludedInSubtree = useMemo(() => {
    let n = 0;
    for (const a of node.addresses) if (excluded.has(a)) n++;
    return n;
  }, [node.addresses, excluded]);
  const allExcluded = total > 0 && excludedInSubtree === total;
  const someExcluded = excludedInSubtree > 0 && excludedInSubtree < total;
  return (
    <li class="hv-discovery__tree-node">
      <div class="hv-discovery__tree-row">
        <label class="hv-discovery__tree-check" title={allExcluded ? 'Alles ausgeschlossen' : someExcluded ? 'Teils ausgeschlossen' : 'Alles aufgenommen'}>
          <input type="checkbox"
                 disabled={readOnly}
                 checked={!allExcluded}
                 ref={(el) => { if (el) el.indeterminate = someExcluded; }}
                 onChange={() => onToggle(node.addresses, allExcluded)} />
        </label>
        <button type="button" class="hv-discovery__tree-toggle"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}>
          <span class="hv-discovery__tree-caret">{open ? '▾' : '▸'}</span>
          <span class="hv-discovery__tree-label">{node.label}</span>
          <span class="muted hv-discovery__tree-count">
            {excludedInSubtree > 0
              ? `${total - excludedInSubtree}/${total}`
              : `${total}`}
          </span>
        </button>
      </div>
      {open && (
        <TreeChildren node={node} depth={depth}
                      excluded={excluded}
                      readOnly={readOnly}
                      onToggle={onToggle} />
      )}
    </li>
  );
}

interface TreeLeafProps {
  leaf: DiscoveryLeafSuggestion;
  excluded: boolean;
  readOnly: boolean;
  onToggle(include: boolean): void;
}
function TreeLeaf({ leaf, excluded, readOnly, onToggle }: TreeLeafProps) {
  const rangeHint = leaf.minValue !== null && leaf.maxValue !== null
    ? ` [${leaf.minValue} … ${leaf.maxValue}]`
    : '';
  // When notes carries the full backslash-separated Climatix path
  // ("Automation stat.\System\System clock\Hour"), show only the last
  // segment as the leaf's display name — the parent segments already show
  // as the tree's folder path above. Notes without backslashes (BACnet /
  // Modbus display names) render whole.
  const notesDisplay = leaf.notes && leaf.notes.includes('\\')
    ? leaf.notes.split(/\\+/).map((p) => p.trim()).filter(Boolean).pop() ?? leaf.notes
    : leaf.notes;
  return (
    <li class={`hv-discovery__tree-leaf${excluded ? ' hv-discovery__tree-leaf--excluded' : ''}`}>
      <div class="hv-discovery__tree-row">
        <label class="hv-discovery__tree-check">
          <input type="checkbox"
                 disabled={readOnly}
                 checked={!excluded}
                 onChange={() => onToggle(excluded)} />
        </label>
        <div class="hv-discovery__tree-leaf-body">
          <div class="hv-discovery__tree-leaf-title">
            <span class="mono">{leaf.symbol}</span>
            {notesDisplay && <span class="muted"> — {notesDisplay}</span>}
          </div>
          <div class="hv-discovery__tree-leaf-meta muted">
            <span class="mono">{leaf.valueType}</span>
            {leaf.unit && <span> · {leaf.unit}</span>}
            {rangeHint && <span>{rangeHint}</span>}
            {leaf.writable && <span> · rw</span>}
            {leaf.valueType?.toLowerCase() === 'enum' && leaf.enumMembers && leaf.enumMembers.length > 0 && (
              <span title={leaf.enumMembers.map((m) => `${m.value}=${m.displayName ?? m.name}`).join('\n')}>
                {' '}· {leaf.enumMembers.length} Enum-Members
              </span>
            )}
            <span> · <code class="mono">{leaf.address}</code></span>
          </div>
        </div>
      </div>
    </li>
  );
}
